import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentProfile = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [editedProfile, setEditedProfile] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Profile Data from API
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found. Please login again.');
          return;
        }

        const response = await fetch(`${API_LINK}/api/student/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please login again.');
            handleLogout();
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          setProfileData(result.data);
          setEditedProfile(result.data);
        } else {
          setError(result.message || 'Failed to fetch profile data');
        }

      } catch (error) {
        console.error('Error fetching profile data:', error);
        setError('Failed to load profile data. Please refresh the page.');

        // Fallback to userInfo if available
        if (userInfo) {
          const fallbackData = {
            name: userInfo.name || 'Student User',
            studentId: userInfo.id || 'SVU2024001',
            email: '',
            phone: '',
            course: '',
            department: '',
            year: '',
            batch: '',
            address: '',
            dob: '',
            rollNumber: '',
            regNumber: '',
            parentsNumber: '',
            profileImage: ''
          };
          setProfileData(fallbackData);
          setEditedProfile(fallbackData);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userInfo]);

  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };


  // Handle profile edit
  const handleEdit = () => {
    setIsEditing(true);
    setSaveMessage(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found. Please login again.');
        return;
      }

      // Validate required fields
      if (!editedProfile.name || editedProfile.name.trim() === '') {
        setSaveError('Name is required');
        return;
      }

      // Validate email format if provided
      if (editedProfile.email && editedProfile.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editedProfile.email)) {
          setSaveError('Please enter a valid email address');
          return;
        }
      }

      // Validate phone number if provided
      if (editedProfile.phone && editedProfile.phone.trim() !== '') {
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(editedProfile.phone.replace(/\D/g, ''))) {
          setSaveError('Phone number must be exactly 10 digits');
          return;
        }
      }

      // Create FormData for file upload
      const formData = new FormData();

      // Add profile data as JSON string
      const profileUpdateData = {
        name: editedProfile.name.trim(),
        email: editedProfile.email ? editedProfile.email.trim() : '',
        phone: editedProfile.phone ? editedProfile.phone.replace(/\D/g, '') : '',
        address: editedProfile.address ? editedProfile.address.trim() : '',
        dob: editedProfile.dob || ''
      };

      formData.append('profileData', JSON.stringify(profileUpdateData));

      // Add image file if selected
      if (imageFile) {
        formData.append('profileImage', imageFile);
        console.log('📁 Adding image file to upload:', imageFile.name, 'Size:', imageFile.size);
      }

      console.log('🔄 Sending profile update with FormData');

      const response = await fetch(`${API_LINK}/api/student/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });

      console.log('📡 Response status:', response.status);

      if (!response.ok) {
        if (response.status === 401) {
          setSaveError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (response.status === 400) {
          const errorData = await response.json();
          setSaveError(errorData.message || 'Invalid data provided');
          return;
        } else if (response.status === 404) {
          setSaveError('Profile not found. Please contact support.');
          return;
        }

        const errorData = await response.text();
        console.error('❌ API Error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Save result:', result);

      if (result.success && result.data) {
        setProfileData(result.data);
        setEditedProfile(result.data);
        setIsEditing(false);
        setImageFile(null);
        setImagePreview(null);
        setSaveMessage('Profile updated successfully!');

        // Clear success message after 5 seconds
        setTimeout(() => setSaveMessage(null), 5000);
      } else {
        throw new Error(result.message || 'Failed to update profile');
      }

    } catch (error) {
      console.error('💥 Error saving profile:', error);
      setSaveError(`Failed to save profile: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedProfile(profileData);
    setImageFile(null);
    setImagePreview(null);
    setSaveMessage(null);
    setSaveError(null);
  };

  const handleInputChange = (field, value) => {
    // Extra security: prevent editing of restricted fields
    const allowedFields = ['name', 'email', 'phone', 'address', 'dob'];
    if (!allowedFields.includes(field)) {
      console.warn(`Attempt to edit restricted field: ${field}`);
      return;
    }

    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setSaveError('Image file size must be less than 5MB');
        event.target.value = ''; // Clear the input
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setSaveError('Please select a valid image file');
        event.target.value = ''; // Clear the input
        return;
      }

      setImageFile(file);
      setSaveError(null); // Clear any previous errors

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      console.log('📷 Image selected:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');

      // If not in editing mode, upload immediately
      if (!isEditing) {
        handleDirectPhotoUpload(file);
      }
    }
  };

  // Direct photo upload without entering edit mode
  const handleDirectPhotoUpload = async (file) => {
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveMessage(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setSaveError('Authentication token not found. Please login again.');
        return;
      }

      const formData = new FormData();
      formData.append('profileImage', file);

      // Send current profile data along with the image
      const profileUpdateData = {
        name: profileData.name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        dob: profileData.dob || ''
      };
      formData.append('profileData', JSON.stringify(profileUpdateData));

      const response = await fetch(`${API_LINK}/api/student/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        setProfileData(result.data);
        setEditedProfile(result.data);
        setImageFile(null);
        setImagePreview(null);
        setSaveMessage('Profile photo updated successfully!');
        setTimeout(() => setSaveMessage(null), 5000);
      } else {
        throw new Error(result.message || 'Failed to upload photo');
      }

    } catch (error) {
      console.error('Error uploading photo:', error);
      setSaveError(`Failed to upload photo: ${error.message}`);
      setImageFile(null);
      setImagePreview(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    // Reset the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Delete profile image from server
  const handleDeleteProfileImage = async () => {
    if (!profileData.profileImage && !imagePreview) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        setSaveError('Authentication token not found');
        return;
      }

      const response = await fetch(`${API_LINK}/api/student/profile/image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setProfileData(prev => ({ ...prev, profileImage: null }));
        setEditedProfile(prev => ({ ...prev, profileImage: null }));
        handleImageRemove(); // Clear preview and file
        setSaveMessage('Profile image deleted successfully');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setSaveError(errorData.message || 'Failed to delete image');
      }

    } catch (error) {
      console.error('Error deleting profile image:', error);
      setSaveError('Failed to delete profile image');
    }
  };

  // Get profile image URL (with preview support for profile upload)
  const getProfileImageUrl = () => {
    if (imagePreview) {
      return imagePreview; // Use preview for uploaded image
    }
    if (profileData.profileImage) {
      return `${API_LINK}${profileData.profileImage}`;
    }
    return null;
  };

  // Get user initials for profile display
  const getUserInitials = (name) => {
    if (!name) return 'ST';
    const names = name.split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format year to academic year display
  const formatYear = (year) => {
    if (!year) return 'Not provided';
    const yearNum = parseInt(year);
    if (isNaN(yearNum)) return year; // Return as-is if not a number

    // Only format valid academic years (1-4)
    if (yearNum < 1 || yearNum > 4) {
      return year.toString(); // Return as-is for other numeric values
    }

    // Convert to ordinal (1st, 2nd, 3rd, 4th)
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const relevantDigit = yearNum % 10;
    const suffix = (relevantDigit <= 3 && yearNum !== 11 && yearNum !== 12 && yearNum !== 13)
      ? suffixes[relevantDigit]
      : suffixes[0];

    return `${yearNum}${suffix} Year`;
  };



  const ProfileField = ({ label, value, field, type = "text", readonly = false }) => (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '0.3rem' : '1rem',
      alignItems: isMobile ? 'flex-start' : 'center',
      padding: isMobile ? '0.7rem 0.8rem' : '1rem',
      borderBottom: '1px solid #ecf0f1',
      transition: 'background-color 0.3s ease',
      backgroundColor: readonly ? '#f8f9fa' : 'transparent'
    }}>
      <label style={{
        fontWeight: '600',
        color: '#333',
        minWidth: isMobile ? 'auto' : '200px',
        fontSize: isMobile ? '0.8rem' : '0.95rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        {label}
        {readonly && (
          <span style={{
            fontSize: '0.7rem',
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            padding: '0.2rem 0.4rem',
            borderRadius: '4px',
            fontWeight: 'normal'
          }}>
            🔒 Protected
          </span>
        )}
      </label>
      {isEditing && !readonly ? (
        type === 'textarea' ? (
          <textarea
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            rows={3}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '2px solid #97d6ac',
              borderRadius: '8px',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease',
              backgroundColor: '#fff',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        ) : (
          <input
            type={type}
            value={editedProfile[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: '2px solid #97d6ac',
              borderRadius: '8px',
              fontSize: '0.95rem',
              transition: 'all 0.3s ease',
              backgroundColor: '#fff'
            }}
          />
        )
      ) : (
        <span style={{
          flex: 1,
          color: readonly ? '#6c757d' : '#666',
          fontSize: isMobile ? '0.85rem' : '0.95rem',
          padding: isMobile ? '0.3rem 0' : '0.75rem 0',
          fontStyle: readonly ? 'italic' : 'normal'
        }}>
          {value || 'Not provided'}
        </span>
      )}
    </div>
  );

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Profile...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Profile
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            For any further information, please contact your support team.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-profile"
      pageTitle="Student Profile"
    >



      {/* Profile Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: isMobile ? '1.2rem 1rem' : '2rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '0.8rem' : '1rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '2rem',
            flexWrap: 'wrap'
          }}>
            {/* Profile Image with Upload */}
            <div style={{ position: 'relative' }}>
              <div style={{
                width: isMobile ? '80px' : '120px',
                height: isMobile ? '80px' : '120px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                border: '4px solid rgba(255, 255, 255, 0.3)',
                flexShrink: 0,
                overflow: 'hidden',
                backgroundImage: getProfileImageUrl() ? `url(${getProfileImageUrl()})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}>
                {!getProfileImageUrl() && getUserInitials(profileData.name)}
              </div>

              {/* Image Upload Button - Always visible */}
              <div style={{
                position: 'absolute',
                bottom: isMobile ? '2px' : '10px',
                right: isMobile ? '2px' : '10px',
                display: 'flex',
                gap: isMobile ? '0.3rem' : '0.5rem'
              }}>
                <label style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? '26px' : '35px',
                  height: isMobile ? '26px' : '35px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSaving ? 'wait' : 'pointer',
                  fontSize: isMobile ? '0.7rem' : '1rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  opacity: isSaving ? 0.6 : 1
                }}>
                  {isSaving ? '⏳' : '📷'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={isSaving}
                  />
                </label>

                {(imagePreview || profileData.profileImage) && (
                  <button
                    onClick={imagePreview ? handleImageRemove : handleDeleteProfileImage}
                    disabled={isSaving}
                    style={{
                      background: 'rgba(255, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: '50%',
                      width: isMobile ? '26px' : '35px',
                      height: isMobile ? '26px' : '35px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isSaving ? 'wait' : 'pointer',
                      fontSize: isMobile ? '0.7rem' : '1rem',
                      color: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease',
                      opacity: isSaving ? 0.6 : 1
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>

            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h1 style={{
                margin: isMobile ? '0 0 0.3rem 0' : '0 0 0.5rem 0',
                fontSize: isMobile ? '1.3rem' : '2.2rem',
                fontWeight: 'bold'
              }}>
                {profileData.name || 'Student Name'}
              </h1>
              <p style={{
                margin: '0 0 0.3rem 0',
                fontSize: isMobile ? '0.85rem' : '1.2rem',
                opacity: 0.9
              }}>
                ID: {profileData.studentId || 'Student ID'}
              </p>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '0.75rem' : '1rem',
                opacity: 0.8
              }}>
                {profileData.course || 'Course'} • {profileData.department || 'Department'}
              </p>
            </div>
          </div>
          {/* Read-only badge */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexShrink: 0
          }}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                padding: isMobile ? '0.5rem 1rem' : '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: isMobile ? '0.8rem' : '1rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              🔒 Read Only
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {(saveMessage || saveError) && (
        <div style={{
          background: saveMessage ? '#d4edda' : '#f8d7da',
          border: `1px solid ${saveMessage ? '#c3e6cb' : '#f5c6cb'}`,
          color: saveMessage ? '#155724' : '#721c24',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1.2rem' }}>
            {saveMessage ? '✅' : '❌'}
          </span>
          {saveMessage || saveError}
        </div>
      )}

      {/* Profile Information */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: isMobile ? '1rem' : '2rem'
      }}>
        {/* Personal Information */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem 1rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>👤</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Personal Information</h3>
          </div>
          <div>
            <ProfileField label="Full Name" value={profileData.name} field="name" readonly />
            <ProfileField label="Date of Birth" value={formatDate(profileData.dob)} field="dob" type="date" readonly />
            <ProfileField label="Email Address" value={profileData.email} field="email" type="email" readonly />
            <ProfileField label="Phone Number" value={profileData.phone} field="phone" type="tel" readonly />
            <ProfileField label="Address" value={profileData.address} field="address" type="textarea" readonly />
            <ProfileField label="Parents Number" value={profileData.parentsNumber} field="parentsNumber" type="tel" readonly />
          </div>
        </div>

        {/* Academic Information */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: isMobile ? '0.8rem 1rem' : '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '0.5rem' : '1rem'
          }}>
            <span style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>🎓</span>
            <h3 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.2rem' }}>Academic Information</h3>
          </div>
          <div>
            <ProfileField label="Student ID" value={profileData.studentId} field="studentId" readonly />
            <ProfileField label="Roll Number" value={profileData.rollNumber} field="rollNumber" readonly />
            <ProfileField label="Registration Number" value={profileData.regNumber} field="regNumber" readonly />
            <ProfileField label="Course" value={profileData.course} field="course" readonly />
            <ProfileField label="Department" value={profileData.department} field="department" readonly />
            <ProfileField label="Year" value={formatYear(profileData.year)} field="year" readonly />
            <ProfileField label="Batch" value={profileData.batch} field="batch" readonly />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentProfile;